import * as React from "react";
import BottomDrawer from ".";
import Button from "../button";
import { holeInCourseOf } from "../../../lib/utils/score.utils";
import type { DrawerCore } from "../../../pages/score/detail";

type Props = {
  open: boolean;
  onClose: () => void;
  data: DrawerCore;
  courseName?: string;
  onSubmit: (par: number) => void;
};

const PAR_PRESETS = [3, 4, 5];

export default function ParPersonalDrawer({
  open,
  onClose,
  data,
  courseName,
  onSubmit,
}: Props) {
  const { holeNo, par } = data;

  const [parValue, setParValue] = React.useState<number>(NaN);

  React.useEffect(() => {
    if (!open) return;
    if (typeof par === "number" && !Number.isNaN(par) && par > 0) {
      setParValue(par);
    } else {
      setParValue(4);
    }
  }, [open, par]);

  return (
    <BottomDrawer
      open={open}
      onClose={onClose}
      maxHeightRatio={1}
      className="rounded-t-[20px] overflow-hidden"
    >
      <div className="bg-white pb-[88px]">
        {/* 상단: 홀 정보 */}
        <div className="mt-[24px] mb-[18px] flex items-center justify-center px-2">
          <div className="min-w-0 text-center font-[500] text-[1.375rem] leading-snug break-keep">
            <span>{courseName} </span>
            <span className="text-[#222]">{holeInCourseOf(holeNo)}홀</span>
          </div>
        </div>

        {/* 프리셋 + 스텝퍼 */}
        <div className="mt-[10px] space-y-3">
          <div className="mb-[35px] flex gap-[13px]">
            <div className="bg-[#f6f6f6] rounded-[15px] p-[19px] flex-1 min-w-0">
              {PAR_PRESETS.map((value, i) => {
                const selected = parValue === value;
                return (
                  <div
                    key={i}
                    className="mb-[10px] flex items-center gap-[8px] font-[500] leading-snug text-[1.25rem] last:mb-0"
                  >
                    <span className="min-w-0 flex-1 break-keep text-right text-[#222]">
                      파{value}
                    </span>
                    <button
                      type="button"
                      onClick={() => setParValue(value)}
                      className={`w-[min(6.25rem,30vw)] min-h-[3.125rem] shrink-0 rounded-[10px] border px-2 text-center font-bold leading-none text-[1.25rem] transition
                        ${selected ? "bg-white shadow-[0_6px_12px_rgba(0,0,0,0.15)] border-[#222]" : "bg-white border-[#e6e6e6]"}`}
                    >
                      {value}
                    </button>
                    <span className="min-w-0 flex-1" />
                  </div>
                );
              })}

              {/* 스텝퍼 */}
              <div className="mt-[15px]">
                <div className="mx-auto flex w-full max-w-[245px] items-center justify-between gap-2 rounded-full bg-white px-4 py-2">
                  <button
                    type="button"
                    disabled={parValue <= 2}
                    className="w-fit self-stretch flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                    onClick={() => setParValue((p) => (p > 2 ? p - 1 : 2))}
                    aria-label="감소"
                  >
                    <img
                      className="w-6"
                      src="/icons/ic-minus.webp"
                      alt="minus"
                    />
                  </button>

                  <span className="min-w-0 text-center font-bold leading-none text-[#222] select-none text-[1.75rem]">
                    {Number.isFinite(parValue) ? parValue : 4}
                  </span>

                  <button
                    type="button"
                    disabled={parValue >= 7}
                    className="w-fit self-stretch flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                    onClick={() =>
                      setParValue((p) =>
                        Number.isFinite(p) ? Math.min(p + 1, 7) : 7,
                      )
                    }
                    aria-label="증가"
                  >
                    <img
                      className="size-6"
                      src="/icons/ic_plus.png"
                      alt="plus"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[720px] bg-white px-[15px] py-[10px]">
          <Button
            variant="red"
            className="h-auto min-h-[50px] w-full whitespace-normal px-4 py-3 font-bold"
            onClick={() => {
              const finalPar = Number.isFinite(parValue) ? parValue : 4;
              onSubmit(finalPar);
            }}
          >
            <span className="min-w-0 break-keep text-center leading-snug">
              {courseName} {holeInCourseOf(holeNo)}홀 파 입력 완료
            </span>
          </Button>
        </div>
      </div>
    </BottomDrawer>
  );
}
